import React, { useEffect, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Chip,
} from "@mui/material";
import {
    Construction,
    Download,
    NewReleases,
} from "@mui/icons-material";
import { useReferentialContext } from "../../Context";
import { NeonCard, NeonCardHeader, NeonCardContent } from "../../NeonComponents/NeonCards";
import { getNeonColor } from "../../theme";
import { useTheme } from "@mui/system";
import { NeonIconBox } from "../../NeonComponents/NeonIconBox";

interface AppRelease {
    version: string;
    date?: string;
    installer?: string;
    notes?: string;
    is_latest: boolean;
}

export default function UtilsPage() {
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const [releases, setReleases] = useState<AppRelease[]>([]);
    const [loadingReleases, setLoadingReleases] = useState(true);
    const [releasesError, setReleasesError] = useState<string | null>(null);
    const [downloadingVersion, setDownloadingVersion] = useState<string | null>(null);

    useEffect(() => {
        const fetchReleases = async () => {
            try {
                const res = await fetch(
                    `${context.reckonApi.url}/App/releases`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Token ${context.reckonApi.user_infos.token}`,
                        },
                    },
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const latestVersion: string = data.latest ?? "";
                const versions: Array<{ version: string; date?: string; installer?: string; notes?: string }> =
                    data.versions ?? [];
                setReleases(
                    versions.map((v) => ({
                        ...v,
                        is_latest: v.version === latestVersion,
                    })),
                );
            } catch (e: any) {
                console.warn("Failed to fetch releases:", e);
                setReleasesError(e?.message ?? "Failed to fetch releases.");
            } finally {
                setLoadingReleases(false);
            }
        };
        fetchReleases();
    }, []);

    const handleDownload = async (release: AppRelease) => {
        setDownloadingVersion(release.version);
        try {
            await context.reckonApi.download_file(
                `App/download/${encodeURIComponent(release.version)}`,
                {},
                release.installer ?? `reckon-bolt-${release.version}.exe`,
            );
        } catch (e: any) {
            console.error("Download failed:", e);
        } finally {
            setDownloadingVersion(null);
        }
    };

    const latestRelease = releases.find((r) => r.is_latest) ?? releases[0];

    return (
        <Box sx={{ width: "100%", padding: 4, maxWidth: "1100px", margin: "0 auto" }}>
            {/* Page Header */}
            <Stack direction="row" spacing={2} sx={{ mb: 4, alignItems: "center" }}>
                <NeonIconBox>
                    <Construction />
                </NeonIconBox>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Utils
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Application downloads and tools
                    </Typography>
                </Box>
            </Stack>

            {/* App Download Section */}
            <NeonCard>
                <NeonCardHeader
                    avatar={
                        <Download
                            sx={{
                                color: neonColor,
                                filter: `drop-shadow(0 0 6px ${neonColor})`,
                            }}
                        />
                    }
                    title="Reckon Bolt App"
                    subheader="Download the desktop application"
                />
                <NeonCardContent>
                    {loadingReleases && (
                        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                            <CircularProgress size={32} sx={{ color: neonColor }} />
                            <Typography variant="body2" color="text.secondary">
                                Fetching latest release...
                            </Typography>
                        </Stack>
                    )}

                    {releasesError && (
                        <Alert
                            severity="error"
                            sx={{ borderRadius: 2 }}
                            onClose={() => setReleasesError(null)}
                        >
                            {releasesError}
                        </Alert>
                    )}

                    {!loadingReleases && !releasesError && !latestRelease && (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No releases available yet.
                        </Typography>
                    )}

                    {!loadingReleases && latestRelease && (
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <NewReleases sx={{ color: "#66bb6a", fontSize: 28 }} />
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        v{latestRelease.version}
                                    </Typography>
                                    <Chip
                                        label="Latest"
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: "0.7rem",
                                            bgcolor: "#66bb6a20",
                                            color: "#66bb6a",
                                            border: "1px solid #66bb6a40",
                                        }}
                                    />
                                </Stack>
                                {latestRelease.date && (
                                    <Typography variant="caption" color="text.secondary">
                                        {latestRelease.date}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                onClick={() => handleDownload(latestRelease)}
                                disabled={downloadingVersion === latestRelease.version}
                                startIcon={
                                    downloadingVersion === latestRelease.version
                                        ? <CircularProgress size={16} />
                                        : <Download />
                                }
                                sx={{
                                    borderColor: `${neonColor}60`,
                                    color: neonColor,
                                    textTransform: "none",
                                    minWidth: 140,
                                    "&:hover": {
                                        borderColor: neonColor,
                                        bgcolor: `${neonColor}10`,
                                        boxShadow: `0 0 12px ${neonColor}25`,
                                    },
                                }}
                            >
                                {downloadingVersion === latestRelease.version ? "Downloading..." : "Download"}
                            </Button>
                        </Stack>
                    )}
                </NeonCardContent>
            </NeonCard>
        </Box>
    );
}
